import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Chip,
  InputAdornment,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link as RouterLink } from "react-router-dom";

import { CostAnalysisLine } from "../../store/apis/costAnalysisApi";
import {
  compact,
  money,
  monthLabel,
  pct,
  unitMoney,
  valueLabel,
  VOLUME_LABEL,
} from "./costAnalysisFormat";

const PAGE_SIZE = 50;

interface Column {
  id: string;
  label: string;
  hint?: string;
  numeric: boolean;
  sortValue: (l: CostAnalysisLine) => number | string | null;
  render: (l: CostAnalysisLine) => React.ReactNode;
}

const Sub: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography variant="caption" color="text.secondary" display="block">
    {children}
  </Typography>
);

/** "ago 2026" o "ago – oct 2026" */
const monthsLabel = (months: string[]): string => {
  if (months.length === 0) return "—";
  const first = monthLabel(months[0]);
  if (months.length === 1) return first;
  return `${first} – ${monthLabel(months[months.length - 1])}`;
};

const COLUMNS: Column[] = [
  {
    id: "line",
    label: "Línea",
    numeric: false,
    sortValue: (l) => l.lineName,
    render: (l) => (
      <Box sx={{ minWidth: 220, maxWidth: 360, whiteSpace: "normal" }}>
        <Link
          component={RouterLink}
          to={`/briefings/${l.briefingId}/associated-campaigns/${l.lineId}`}
          target="_blank"
          rel="noopener"
          underline="hover"
          sx={{ fontWeight: 600, display: "inline-flex", gap: 0.5 }}
        >
          {l.lineName}
          <OpenInNewIcon sx={{ fontSize: 14, mt: "3px" }} />
        </Link>
        <Sub>
          {l.company} · {l.briefingName}
        </Sub>
      </Box>
    ),
  },
  {
    id: "platform",
    label: "Plataforma",
    numeric: false,
    sortValue: (l) => valueLabel("platform", l.platform),
    render: (l) => (
      <>
        {valueLabel("platform", l.platform)}
        <Sub>
          {valueLabel("campaignType", l.campaignType)}
          {l.isDirectBuy ? " · compra directa" : ""}
        </Sub>
      </>
    ),
  },
  {
    id: "months",
    label: "Meses",
    numeric: false,
    sortValue: (l) => l.months[0] ?? null,
    render: (l) => monthsLabel(l.months),
  },
  {
    id: "volume",
    label: "Volumen",
    numeric: true,
    sortValue: (l) => l.volume,
    render: (l) => (
      <>
        {compact(l.volume)}
        <Sub>{VOLUME_LABEL[l.objective] ?? ""}</Sub>
      </>
    ),
  },
  {
    id: "buyUnit",
    label: "Compra",
    hint: "A cuánto se ha comprado: coste real por unidad del objetivo de la línea (por mil en CPM). Debajo, la prevista en el goal",
    numeric: true,
    sortValue: (l) => l.realUnitCost,
    render: (l) =>
      l.realUnitCost === null ? (
        "—"
      ) : (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {unitMoney(l.realUnitCost)}{" "}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              {l.objective}
            </Typography>
          </Typography>
          {l.plannedNetUnitCost !== null && (
            <Sub>plan {unitMoney(l.plannedNetUnitCost)}</Sub>
          )}
        </>
      ),
  },
  {
    id: "saleUnit",
    label: "Venta",
    hint: "A cuánto se ha vendido: lo facturado por unidad del objetivo. Debajo, el precio del briefing",
    numeric: true,
    sortValue: (l) => l.saleUnitCost,
    render: (l) =>
      l.saleUnitCost === null ? (
        "—"
      ) : (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {unitMoney(l.saleUnitCost)}{" "}
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              {l.objective}
            </Typography>
          </Typography>
          {l.plannedGrossUnitCost !== null && (
            <Sub>briefing {unitMoney(l.plannedGrossUnitCost)}</Sub>
          )}
        </>
      ),
  },
  {
    id: "margin",
    label: "Margen real",
    hint: "1 − coste real / venta. Debajo, el margen puesto en el goal de la línea",
    numeric: true,
    sortValue: (l) => l.marginPercent,
    render: (l) => (
      <>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {pct(l.marginPercent)}
        </Typography>
        {l.lineMargin !== null && <Sub>puesto {pct(l.lineMargin)}</Sub>}
      </>
    ),
  },
  {
    id: "realCost",
    label: "Coste real",
    numeric: true,
    sortValue: (l) => l.realCost,
    render: (l) => money(l.realCost),
  },
  {
    id: "sale",
    label: "Facturado",
    hint: "Coste de cliente con el margen diario aplicado",
    numeric: true,
    sortValue: (l) => l.sale,
    render: (l) => (
      <>
        {money(l.sale)}
        {l.saleFromPlan && (
          <Tooltip title="Parte de la venta se ha estimado con el margen del plan: la campaña no tenía margen configurado.">
            <Chip size="small" label="estimada" sx={{ ml: 0.5, height: 18 }} />
          </Tooltip>
        )}
      </>
    ),
  },
  {
    id: "profit",
    label: "Beneficio",
    numeric: true,
    sortValue: (l) => l.grossProfit,
    render: (l) => money(l.grossProfit),
  },
];

const normalize = (text: string) =>
  text.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

interface Props {
  lines: CostAnalysisLine[];
}

/** Las líneas de briefing que hay detrás de lo filtrado, una fila por línea. */
const CostAnalysisLines: React.FC<Props> = ({ lines }) => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);
  // Sin orden elegido, el del backend: de más a menos coste real.
  const [sort, setSort] = useState<{ id: string; dir: "asc" | "desc" } | null>(
    null
  );

  const rows = useMemo(() => {
    const term = normalize(search.trim());
    const found = term
      ? lines.filter((l) =>
          normalize(`${l.lineName} ${l.briefingName} ${l.company}`).includes(
            term
          )
        )
      : lines;
    if (!sort) return found;
    const column = COLUMNS.find((c) => c.id === sort.id);
    const factor = sort.dir === "asc" ? 1 : -1;
    return [...found].sort((a, b) => {
      const va = column?.sortValue(a) ?? null;
      const vb = column?.sortValue(b) ?? null;
      // Los vacíos siempre al final, en los dos sentidos.
      if (va === null && vb === null) return 0;
      if (va === null) return 1;
      if (vb === null) return -1;
      if (typeof va === "string" || typeof vb === "string") {
        return factor * String(va).localeCompare(String(vb), "es");
      }
      return factor * (va - vb);
    });
  }, [lines, search, sort]);

  const toggleSort = (column: Column) =>
    setSort((s) =>
      s?.id === column.id
        ? { id: column.id, dir: s.dir === "asc" ? "desc" : "asc" }
        : { id: column.id, dir: column.numeric ? "desc" : "asc" }
    );

  const shown = rows.slice(0, visible);

  return (
    <Stack gap={1.5}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        gap={1}
      >
        <Typography variant="subtitle1" fontWeight={600}>
          {lines.length} {lines.length === 1 ? "línea" : "líneas"}
          {search.trim() && rows.length !== lines.length
            ? ` · ${rows.length} con "${search.trim()}"`
            : ""}
        </Typography>
        <TextField
          size="small"
          placeholder="Buscar línea, briefing o cliente"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setVisible(PAGE_SIZE);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-label": "Buscar línea" }}
          sx={{ width: { xs: "100%", sm: 320 } }}
        />
      </Stack>

      <TableContainer
        sx={{
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 3,
          maxHeight: "70vh",
        }}
      >
        <Table size="small" stickyHeader aria-label="Líneas">
          <TableHead>
            <TableRow>
              {COLUMNS.map((c) => (
                <TableCell
                  key={c.id}
                  align={c.numeric ? "right" : "left"}
                  sortDirection={sort?.id === c.id ? sort.dir : false}
                  sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
                >
                  <Tooltip
                    title={c.hint ?? ""}
                    disableHoverListener={!c.hint}
                    describeChild
                  >
                    <TableSortLabel
                      active={sort?.id === c.id}
                      direction={
                        sort?.id === c.id
                          ? sort.dir
                          : c.numeric
                            ? "desc"
                            : "asc"
                      }
                      onClick={() => toggleSort(c)}
                    >
                      {c.label}
                    </TableSortLabel>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((l) => (
              <TableRow key={l.lineId} hover>
                {COLUMNS.map((c) => (
                  <TableCell
                    key={c.id}
                    align={c.numeric ? "right" : "left"}
                    sx={{ whiteSpace: "nowrap", verticalAlign: "top" }}
                  >
                    {c.render(l)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {shown.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length}>
                  <Typography variant="body2" color="text.secondary">
                    Ninguna línea coincide con la búsqueda.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {rows.length > visible && (
        <Stack direction="row" gap={1} justifyContent="center">
          <Button size="small" onClick={() => setVisible((v) => v + PAGE_SIZE)}>
            Ver {Math.min(PAGE_SIZE, rows.length - visible)} más
          </Button>
          <Button size="small" onClick={() => setVisible(rows.length)}>
            Ver todas ({rows.length})
          </Button>
        </Stack>
      )}
    </Stack>
  );
};

export default CostAnalysisLines;
